import React from "react";
import { faUser, faUserTie } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Grid, Typography, Box } from "@mui/material";
import { useSelector } from "react-redux";

const FallbackMessage = ({ message }) => {
  return (
    <Box
      sx={{
        p: 2,
        textAlign: "center",
        color: "#64748B",
        fontFamily: "Roboto, Helvetica, Arial, sans-serif",
        fontSize: "13px",
        border: "1px dashed #CBD5E1",
        borderRadius: "8px",
        bgcolor: "#F8FAFC",
      }}
    >
      {message}
    </Box>
  );
};

const CaseDetailAdvocates = () => {
  const caseDetail = useSelector((state) => state.caseDetail);
  const { caseItem } = caseDetail;
  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 2,
        p: 1.5,
        boxSizing: "border-box",
        bgcolor: "#FFFFFF",
        borderRadius: "12px",
        position: "relative",
        overflow: "auto",
      }}
    >
      <Grid container spacing={2}>
        {/* Appellant's Advocates */}
        <Grid item xs={12} md={6}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1.5,
              height: "100%",
              position: "relative",
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: "2px",
                background: "linear-gradient(90deg, #2563EB, #60A5FA)",
                opacity: 0.8,
              },
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                p: 1.25,
                bgcolor: "#FFFFFF",
                borderRadius: "8px",
              }}
            >
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: "#EFF6FF",
                  borderRadius: "8px",
                  color: "#2563EB",
                }}
              >
                <FontAwesomeIcon
                  icon={faUser}
                  style={{ fontSize: "0.875rem" }}
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    color: "#1E293B",
                    fontSize: "0.875rem",
                    fontWeight: 600,
                    mb: 0.25,
                  }}
                >
                  Appellant's Advocates
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: "#64748B",
                    fontSize: "0.75rem",
                  }}
                >
                  Legal team representing the appellant
                </Typography>
              </Box>
            </Box>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1,
                maxHeight: "calc(100vh - 200px)",
                overflowY: "auto",
                pr: 1,
                "&::-webkit-scrollbar": {
                  width: "4px",
                },
                "&::-webkit-scrollbar-track": {
                  background: "transparent",
                },
                "&::-webkit-scrollbar-thumb": {
                  background: "#CBD5E1",
                  borderRadius: "2px",
                },
              }}
            >
              {caseItem?.Appeallant_Lawyer ? (
                caseItem?.Appeallant_Lawyer?.split(",").map(
                  (advocate, index) => (
                    <Box
                      key={index}
                      sx={{
                        p: 1.25,
                        bgcolor: "#FFFFFF",
                        borderRadius: "8px",
                        border: "1px solid #E2E8F0",
                        display: "flex",
                        gap: 1.25,
                        alignItems: "center",
                        transition: "all 0.2s ease",
                        "&:hover": {
                          borderColor: "#2563EB",
                          bgcolor: "#F8FAFC",
                          boxShadow: "0 2px 8px rgba(37, 99, 235, 0.08)",
                        },
                      }}
                    >
                      <Box
                        sx={{
                          width: 28,
                          height: 28,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          bgcolor: "#EFF6FF",
                          borderRadius: "6px",
                          color: "#2563EB",
                          fontSize: "0.75rem",
                          fontWeight: 600,
                          flexShrink: 0,
                        }}
                      >
                        {index + 1}
                      </Box>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          color: "#1E293B",
                          fontSize: "0.8125rem",
                          fontWeight: 600,
                        }}
                      >
                        {caseItem?.Appeallant_Lawyer ? advocate : advocate.name}
                      </Typography>
                    </Box>
                  )
                )
              ) : (
                <FallbackMessage message="No advocates available for the appellant." />
              )}
            </Box>
          </Box>
        </Grid>

        {/* Respondent's Advocates */}
        <Grid item xs={12} md={6}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1.5,
              height: "100%",
              position: "relative",
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: "2px",
                background: "linear-gradient(90deg, #DC2626, #F87171)",
                opacity: 0.8,
              },
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                p: 1.25,
                bgcolor: "#FFFFFF",
                borderRadius: "8px",
              }}
            >
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: "#FEE2E2",
                  borderRadius: "8px",
                  color: "#DC2626",
                }}
              >
                <FontAwesomeIcon
                  icon={faUserTie}
                  style={{ fontSize: "0.875rem" }}
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    color: "#1E293B",
                    fontSize: "0.875rem",
                    fontWeight: 600,
                    mb: 0.25,
                  }}
                >
                  Respondent's Advocates
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: "#64748B",
                    fontSize: "0.75rem",
                  }}
                >
                  Legal team representing the respondent
                </Typography>
              </Box>
            </Box>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1,
                maxHeight: "calc(100vh - 200px)",
                overflowY: "auto",
                pr: 1,
                "&::-webkit-scrollbar": {
                  width: "4px",
                },
                "&::-webkit-scrollbar-track": {
                  background: "transparent",
                },
                "&::-webkit-scrollbar-thumb": {
                  background: "#CBD5E1",
                  borderRadius: "2px",
                },
              }}
            >
              {caseItem?.Respondant_Lawyer ? (
                caseItem?.Respondant_Lawyer?.split(",").map(
                  (advocate, index) => (
                    <Box
                      key={index}
                      sx={{
                        p: 1.25,
                        bgcolor: "#FFFFFF",
                        borderRadius: "8px",
                        border: "1px solid #E2E8F0",
                        display: "flex",
                        gap: 1.25,
                        alignItems: "center",
                        transition: "all 0.2s ease",
                        "&:hover": {
                          borderColor: "#DC2626",
                          bgcolor: "#F8FAFC",
                          boxShadow: "0 2px 8px rgba(220, 38, 38, 0.08)",
                        },
                      }}
                    >
                      <Box
                        sx={{
                          width: 28,
                          height: 28,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          bgcolor: "#FEE2E2",
                          borderRadius: "6px",
                          color: "#DC2626",
                          fontSize: "0.75rem",
                          fontWeight: 600,
                          flexShrink: 0,
                        }}
                      >
                        {index + 1}
                      </Box>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          color: "#1E293B",
                          fontSize: "0.8125rem",
                          fontWeight: 600,
                        }}
                      >
                        {caseItem?.Respondant_Lawyer ? advocate : advocate.name}
                      </Typography>
                    </Box>
                  )
                )
              ) : (
                <FallbackMessage message="No advocates available for the respondent." />
              )}
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default CaseDetailAdvocates;
